import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { CosmicBackground } from "@/components/CosmicBackground";
import { TarotSpreadCard } from "@/components/TarotSpreadCard";
import { DeckFan } from "@/components/DeckFan";
import { E } from "@/components/Editable";

const FACTS = [
  "Carl Jung read tarot images as archetypes — shared symbols the mind recognizes before it can explain them.",
  "A shuffled 78-card deck can order itself in more ways than there are atoms in the Milky Way.",
  "The oldest surviving tarot cards were painted in gold leaf for the Visconti family of Milan around 1440.",
  "Psychologists call tarot a 'projective instrument' — like clouds, the cards show you what you're ready to see.",
  "Three cards with orientation give 3,651,648 possible spreads — yours is almost certainly unique tonight.",
];

function renderProse(text) {
  return text.split("\n").map((line, i) => {
    if (/^#{1,4}\s/.test(line)) return <h3 key={i}>{line.replace(/^#{1,4}\s+/, "")}</h3>;
    if (line.trim() === "") return null;
    return <p key={i}>{line.replace(/\*\*(.*?)\*\*/g, "$1")}</p>;
  });
}

export default function ReadingRoom() {
  const [question, setQuestion] = useState("");
  const [phase, setPhase] = useState("idle");
  const [cards, setCards] = useState([null, null, null]);
  const [flipped, setFlipped] = useState([false, false, false]);
  const [text, setText] = useState("");
  const [shuffling, setShuffling] = useState(false);
  const [factIdx, setFactIdx] = useState(() => Math.floor(Math.random() * FACTS.length));

  const busy = phase === "drawing" || phase === "revealing" || phase === "streaming";

  const shuffleDeck = () => {
    if (shuffling || busy) return;
    setShuffling(true);
    setTimeout(() => setShuffling(false), 1100);
  };

  const draw = async () => {
    if (busy) return;
    setPhase("drawing");
    setCards([null, null, null]);
    setFlipped([false, false, false]);
    setText("");
    setFactIdx((i) => (i + 1) % FACTS.length);
    setShuffling(true);
    setTimeout(() => setShuffling(false), 1100);
    try {
      const res = await fetch(`${API}/readings/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });
      if (!res.ok) throw new Error("The cards are unavailable right now.");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.type === "cards") {
            setCards(ev.cards);
            ev.cards.forEach((_, i) =>
              setTimeout(() => setFlipped((f) => f.map((v, j) => (j === i ? true : v))), 900 + i * 700)
            );
            setPhase("revealing");
          } else if (ev.type === "delta") {
            setPhase("streaming");
            setText((t) => t + ev.content);
          } else if (ev.type === "done") {
            setPhase("done");
            toast.success("Reading saved to your archive.");
          } else if (ev.type === "error") {
            throw new Error(ev.message);
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "Something interrupted the reading.");
      setPhase("idle");
    }
  };

  const caption =
    phase === "done"
      ? "Your spread is complete — the reading is below ↓"
      : busy
      ? "The deck is listening..."
      : "The deck is ready — ask your question above";

  return (
    <div className="relative min-h-screen" data-testid="reading-room-page">
      <CosmicBackground />
      <div className="relative z-10 pt-28 pb-10 px-6 max-w-5xl mx-auto text-center">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-gold mb-4"><E k="reading_overline" f="The Reading Room" /></p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter">
          <E k="reading_title" f="Ask, breathe," /> <span className="gold-text italic"><E k="reading_title_accent" f="draw." /></span>
        </h1>
        <p className="mt-4 text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
          <E k="reading_subtext" f="Three cards — one each for work, relationships, and money & prosperity — interpreted by the oracle and saved to your archive." />
        </p>

        <div className="mt-8 max-w-xl mx-auto">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What's on your mind? (optional)"
            data-testid="reading-question-input"
            disabled={busy}
            className="w-full text-center bg-black/30 border border-white/15 rounded-full px-6 py-4 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-gold/60 transition-colors duration-300"
          />
        </div>

        <div className="mt-12 mb-2">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#bdb2d9] font-bold"><E k="reading_didyouknow" f="Did you know" /></p>
          <p className="mt-3 font-heading italic text-lg sm:text-xl text-[#e6dff5] max-w-2xl mx-auto leading-relaxed" data-testid="tarot-fact">
            {FACTS[factIdx]}
          </p>
        </div>

        {phase !== "idle" && (
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto"
          >
            {[0, 1, 2].map((i) => (
              <TarotSpreadCard key={i} card={cards[i]} index={i} flipped={flipped[i]} />
            ))}
          </motion.div>
        )}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-2">
        <DeckFan shuffling={shuffling} caption={caption} />
        <div className="flex justify-center gap-4 mt-6 pb-6">
          <button
            onClick={shuffleDeck}
            disabled={busy}
            data-testid="shuffle-deck-button"
            className="px-8 py-3.5 rounded-full border border-white/25 text-[11px] uppercase tracking-[0.3em] text-[#e6dff5] hover:border-gold/60 hover:text-gold disabled:opacity-40 transition-colors duration-300"
          >
            <E k="reading_btn_shuffle" f="Shuffle the deck" />
          </button>
          <button
            onClick={draw}
            disabled={busy}
            data-testid="draw-cards-button"
            className="px-8 py-3.5 rounded-full border border-gold/60 text-[11px] uppercase tracking-[0.3em] text-gold hover:bg-gold hover:text-black disabled:opacity-40 transition-colors duration-300"
          >
            {busy ? "Reading..." : phase === "done" ? "New reading" : "Draw the cards"}
          </button>
        </div>
      </div>

      {text && (
        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-24">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="glass-panel p-8 lg:p-12 reading-prose text-left"
            data-testid="reading-interpretation"
          >
            {renderProse(text)}
            {phase === "streaming" && <span className="inline-block w-2 h-4 bg-gold animate-pulse-glow ml-1" />}
          </motion.div>
        </div>
      )}
    </div>
  );
}
